import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BookDemoButton } from "@/components/BookDemo";

// Usage-based Toothmatrix Lab pricing (USD, exclusive of VAT).
export const LAB_PRICE_TIERS = [
    { cases: "100", monthly: 99 },
    { cases: "200", monthly: 179 },
    { cases: "300", monthly: 259 },
    { cases: "400", monthly: 339 },
    { cases: "500", monthly: 419 },
    { cases: ">500", monthly: null }, // custom — contact sales
];
export const YEARLY_DISCOUNT = 0.15;

/**
 * Interactive "How many cases per month?" pricing card — slider over the
 * volume tiers + monthly/yearly billing toggle with a live USD price.
 */
export const LabPricingSlider = () => {
    const nav = useNavigate();
    const [idx, setIdx] = useState(0);
    const [yearly, setYearly] = useState(true);

    const tier = LAB_PRICE_TIERS[idx];
    const price = useMemo(() => {
        if (tier.monthly == null) return null;
        const p = yearly ? tier.monthly * (1 - YEARLY_DISCOUNT) : tier.monthly;
        return Number.isInteger(p) ? p.toString() : p.toFixed(2);
    }, [tier, yearly]);

    const contactSales = () => {
        const vol = tier.monthly == null ? "more than 500" : `about ${tier.cases}`;
        nav(`/contact?subject=demo-request-lab&message=${encodeURIComponent(
            `Hi — I'd like to talk pricing for Toothmatrix Lab. My lab produces ${vol} cases per month (${yearly ? "yearly" : "monthly"} billing).`
        )}`);
    };

    return (
        <div className="rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-900/10 p-8" data-testid="lab-pricing-slider">
            <h3 className="text-center text-[22px] font-semibold text-slate-900 tracking-tight">How many cases per month?</h3>

            {/* Billing toggle */}
            <div className="mt-5 flex items-center justify-center gap-6">
                {[["monthly", "Monthly", false], ["yearly", "Yearly", true]].map(([key, label, val]) => (
                    <button key={key} type="button" onClick={() => setYearly(val)} data-testid={`lps-billing-${key}`}
                        className="inline-flex items-center gap-2 text-[14px] text-slate-700">
                        <span className={`h-[18px] w-[18px] rounded-full border-2 grid place-items-center transition-colors ${yearly === val ? "border-cyan-500" : "border-slate-300"}`}>
                            {yearly === val && <span className="h-2 w-2 rounded-full bg-cyan-500" />}
                        </span>
                        {label}
                        {val && <span className="text-cyan-600 font-semibold text-[13px]">(save 15%)</span>}
                    </button>
                ))}
            </div>

            {/* Slider */}
            <div className="mt-6 px-1">
                <input
                    type="range" min={0} max={LAB_PRICE_TIERS.length - 1} step={1} value={idx}
                    onChange={(e) => setIdx(Number(e.target.value))}
                    data-testid="lps-slider"
                    className="w-full h-1.5 rounded-full appearance-none bg-slate-200 accent-cyan-500 cursor-pointer
                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6
                        [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500
                        [&::-webkit-slider-thumb]:shadow-[0_4px_12px_rgba(6,182,212,0.5)]"
                />
                <div className="mt-2 flex justify-between">
                    {LAB_PRICE_TIERS.map((t, i) => (
                        <button key={t.cases} type="button" onClick={() => setIdx(i)} data-testid={`lps-tick-${t.cases.replace(">", "gt")}`}
                            className={`text-[13px] font-medium transition-colors ${i === idx ? "text-cyan-600" : "text-slate-500 hover:text-slate-800"}`}>
                            {t.cases}
                        </button>
                    ))}
                </div>
            </div>

            {/* Price */}
            <div className="mt-8 text-center" data-testid="lps-price">
                {price != null ? (
                    <>
                        <div className="flex items-baseline justify-center gap-2">
                            <span className="text-6xl font-light text-slate-900 tracking-tight">${price}</span>
                            <span className="text-slate-500 text-[16px]">/month</span>
                        </div>
                        <div className="mt-2 text-[12px] text-slate-400">
                            <span className="text-red-500">*</span> exclusive of VAT{yearly ? " · billed annually" : " · cancel any time"}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="text-5xl font-light text-slate-900 tracking-tight">Custom</div>
                        <div className="mt-2 text-[12.5px] text-slate-500">High-volume labs get a tailored plan — talk to us.</div>
                    </>
                )}
            </div>

            {/* CTAs */}
            <button type="button" onClick={contactSales} data-testid="lps-contact-sales"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white text-[15px] font-semibold transition-colors">
                Contact sales <ArrowRight size={15} />
            </button>
            <div className="mt-3 text-center">
                <BookDemoButton testid="lps-book-demo" label="Book a demo appointment"
                    className="inline-flex items-center justify-center gap-2 text-[13.5px] font-medium text-cyan-700 hover:text-cyan-500 transition-colors" />
            </div>
        </div>
    );
};
